import { FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import CollapsibleTable from '@Components/CollapsibleTable';
import ReportsLayout from '@Components/ReportsLayout';
import ScrollToTop from '@Components/ScrollToTop';
import { TableHead } from '@Components/Table';
import CommonConstants from '@Constants/common.constants';
import { getRowsPerPageOptions } from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useRoute from '@Utils/hooks/useRoute.hook';

import CallLogTableProps from './CallLogTable.types';

const CallLogTable: FunctionComponent<CallLogTableProps> = (
  props: CallLogTableProps
): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { changeRoute } = useRoute();

  /** PROPS */
  const { heading, tableProps, ...resProps } = props;
  const { loading, paginationProps, checked, setChecked, ...resTableProps } =
    tableProps;

  /** STATES */
  const [page, setPage] = useState<number>(CommonConstants.INITIAL_PAGE_NO - 1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    CommonConstants.INITIAL_ROWS_PER_PAGE
  );
  const [searchValue, setSearchValue] = useState<string>('');

  useEffect(() => {
    const searchParam =
      query.get(CommonConstants.QUERY_PARAMS.COMMON.SEARCH) || '';
    setSearchValue(`${searchParam}`);

    const pageNo =
      ((query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) as number) ||
        CommonConstants.INITIAL_PAGE_NO) - 1;
    const pageSize =
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) as number) ||
      CommonConstants.INITIAL_ROWS_PER_PAGE;

    setPage(pageNo);
    setRowsPerPage(pageSize);
  }, [location.search]);

  /** EDGE CASE
   * If the user performs an action and there are no more results to show
   * on the particular page then the user is redirected to the previous valid page.
   *
   * Set the pageNo to the previous valid page.
   */
  useEffect(() => {
    const newPageNo = Math.ceil(paginationProps.count / rowsPerPage);

    if (paginationProps.count && newPageNo < page + 1) {
      const pageNoParam = query.set(
        CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
        newPageNo
      );

      changeRoute(pageNoParam);
    }
  }, [paginationProps.count, page, rowsPerPage, changeRoute]);

  /** PAGE HEADING */
  const pageHeading = t('reports.Call Log Report');

  /** PAGINATION HANDLERS --> */
  const rowsPerPageOptions = getRowsPerPageOptions(
    CommonConstants.INITIAL_ROWS_PER_PAGE,
    paginationProps.count
  );

  /** HANDLES PAGE CHANGE */
  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    const searchParam = query.set(
      CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
      newPage + 1
    );
    changeRoute(searchParam);
  };

  /** HANDLES PAGE SIZE CHANGE */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    const searchParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]: newRowsPerPage,
    });
    changeRoute(searchParams);
  };
  /** <-- PAGINATION HANDLERS */

  /** Clears all checkboxes. */
  const clearSelection = () => {
    if (setChecked) {
      setChecked([]);
    }
  };

  /** TABLE HEAD */
  const tableHead: TableHead[] = [
    {
      label: t('reports.Address'),
      width: 14.9,
    },
    {
      label: t('reports.Contact Name'),
      width: 19.2,
    },
    {
      label: t('reports.Phone'),
      width: 14.8,
    },
    {
      label: t('reports.status'),
      width: 12.2,
    },
    {
      label: t('reports.Disposition'),
      width: 14,
    },
    {
      label: t('reports.Call Timing'),
      width: 15.1,
      align: 'center',
    },
    {
      label: t('common.messages.Note'),
      width: 18,
    },
    {
      label: t('reports.transcriptionSummary'),
      width: 22,
      maxWidth: 27,
    },
  ];
  /** <-- TABLE HEADER CHECKBOX HANDLERS */

  /** ON SEARCH HANDLER
   * Sets query to search text and page No to 1
   */
  const onSearch = (value: string) => {
    const searchParam = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.SEARCH]: value,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
    });
    changeRoute(searchParam);

    // Clear bulk selection (if any) when user applies search filter
    clearSelection();
  };

  /** CALL LOG REPORT COLLAPSIBLE TABLE COMPONENT */
  const TableComponent = (
    <CollapsibleTable
      loading={loading}
      tableHead={tableHead}
      checked={checked}
      setChecked={setChecked}
      paginationProps={{
        page,
        rowsPerPage,
        rowsPerPageOptions,
        onPageChange: handleChangePage,
        onRowsPerPageChange: handleChangeRowsPerPage,
        ...paginationProps,
      }}
      {...resTableProps}
    />
  );
  /** <-- TABLE */

  return (
    <>
      <ScrollToTop
        dependencies={[
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
        ]}
      />
      <ReportsLayout
        heading={heading || pageHeading}
        selectedBtnProps={{
          onClick: clearSelection,
          text: checked?.length,
          disabled: loading,
        }}
        clearFilterBtnProps={{
          onClick: clearSelection,
          disabled: loading,
        }}
        searchProps={{
          value: searchValue,
          setValue: setSearchValue,
          onSearch,
        }}
        table={TableComponent}
        {...resProps}
      />
    </>
  );
};

export default CallLogTable;
